import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { getEnrollmentSegment } from "EmployeeApplication/selectors";
import AgeEnrollmentModal, { AgeEnrollmentFormValueProps } from "EmployeeCoverage/Components/EnrollmentModal/AgeEnrollmentModal";
import SegmentEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/SegmentEnrollmentModal";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatNameFL } from "Utils/formatName";
import { EmployeeForm, EmployeePolicy, WholeLifePlan, PlanCost } from "Utils/types";

import Button, { ButtonProps } from "Cobalt/Button";

import { useWholeLifeContext } from "./wholeLife.context";

interface EntitiesToAgeFormInitialValuesProps {
  employeeForm: EmployeeForm;
  employeePolicy: EmployeePolicy;
}
const entitiesToAgeFormInitialValues = ({ employeeForm, employeePolicy }: EntitiesToAgeFormInitialValuesProps): AgeEnrollmentFormValueProps => {
  return {
    employeeEnrolling: employeePolicy.isEnrollingWholeLife,
    employeeFormattedName: formatNameFL(employeeForm),
    dependents: [],
  };
};

export const WholeLifeEnrollmentModals = () => {
  const { plan, planCost, isShowing, hideEnrollmentModal } = useWholeLifeContext();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const employeeDependents = useSelector(getDependents) || [];
  const dispatch = useDispatch();

  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hideEnrollmentModal();
  };

  const onSubmitAgeEnrollment = async (values) => {
    const isEnrollingWholeLife = values.employeeEnrolling;
    const body = {
      id: employeePolicy.id,
      isEnrollingWholeLife: values.employeeEnrolling,
      enrolledWholeLifePlanID: isEnrollingWholeLife === "Yes" ? plan.id : null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseWholeLifePlan"));
    }
  };

  if (planCost?.pricingType === "age") {
    return (
      <AgeEnrollmentModal
        plan={plan}
        employeePolicyID={employeePolicy.id}
        initialValues={entitiesToAgeFormInitialValues({
          employeeForm,
          employeePolicy,
        })}
        title="Enroll In Whole Life Coverage"
        onSubmit={onSubmitAgeEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const currentSegment = getEnrollmentSegment(employeePolicy, employeeDependents, "whole_life");

  const onSubmitSegmentEnrollment = async ({ selectedSegment }) => {
    const body = {
      id: employeePolicy.id,
      isEnrollingWholeLife: selectedSegment === "none" ? "No" : "Yes",
      enrolledWholeLifePlanID: selectedSegment === "none" ? null : plan.id,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseWholeLifePlan"));
    }
  };

  if (planCost?.pricingType === "segment") {
    return (
      <SegmentEnrollmentModal
        plan={plan}
        planCost={planCost}
        title="Enroll In Whole Life Coverage"
        initialValues={{ selectedSegment: currentSegment }}
        onSubmit={onSubmitSegmentEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  return null;
};

interface WholeLifeEnrollOrDeclineButtonProps extends ButtonProps {
  plan: WholeLifePlan;
  planCost: PlanCost;
}

export const WholeLifeEnrollOrDeclineButton = ({ plan, planCost, ...rest }: WholeLifeEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useWholeLifeContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({ plan, planCost });
  };

  return (
    <Button onClick={openModal} {...rest}>
      Enroll or Decline
    </Button>
  );
};

export const WholeLifeIneligibleButton = ({ plan, planCost, ...rest }: WholeLifeEnrollOrDeclineButtonProps) => {
  const dispatch = useDispatch();
  const employeePolicy = useSelector(getEmployeeApplication);
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();

  const declineCoverage = async () => {
    const body = {
      id: employeePolicy.id,
      isEnrollingWholeLife: "No",
      enrolledWholeLifePlanID: null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseWholeLifePlan"));
    }
  };

  return (
    <Button onClick={declineCoverage} {...rest}>
      Continue
    </Button>
  );
};
